// HAȘURA CA GEOMETRIE, nu ca `<pattern>`.
//
// Prima variantă folosea pattern-uri SVG, și arăta bine exact la o mărire. Un
// `<pattern>` e rasterizat de browser într-un tile pe care apoi îl repetă, iar
// rezoluția aleasă pentru tile nu se reface la fiecare zoom: pe un desen ale
// cărui unități sunt METRI, tile-ul se rasterizează minuscul și, mărit, se
// desface în pătrățele. S-a văzut de două ori — o dată ca tablă de șah pe pereți
// (reparată mutând tile-ul pe unități mari cu `patternTransform`), și a doua
// oară, la mărire mare, ca dungi rupte în blocuri. A doua oară nu mai era un
// reglaj: era limita metodei.
//
// Liniile calculate n-au problema asta — sunt vectori, deci rămân drepte la
// orice mărire, în ecran și la export. Costul e că trebuie tăiate la conturul
// zidului, ceea ce se face aici.
//
// UN SINGUR `<path>` PE PERETE. Fiecare segment ar fi putut fi un `<line>`, dar
// un etaj are 913 pereți și ~16 linii de hașură pe fiecare: 15 000 de elemente,
// pe care panoramarea le-ar simți. Adunate într-un `d` cu mai multe `M…L`, ies
// 913 elemente — cât un perete, atât o hașură.

/** Segmentele hașurii unui inel, la unghiul și pasul cerute.
 *
 *  TĂIERE PAR-IMPAR, nu min-max. Prima variantă lua, pe fiecare dreaptă, doar
 *  intrarea cea mai din stânga și ieșirea cea mai din dreapta — corect DOAR pe
 *  un contur convex. Pereții documentului nu sunt: cei mai mari au 18, 21,
 *  chiar 26 de vârfuri, fiindcă ocolesc casa scării și se întorc în L. Pe un L,
 *  min-max trece drept peste scobitură, iar hașura ieșea din zid și traversa
 *  camera — exact dungile lungi de pe desen.
 *
 *  Corect: se strâng TOATE tăieturile dreptei cu laturile, se sortează, și se
 *  iau perechi — 1-2, 3-4, … Între prima și a doua ești înăuntru, între a doua
 *  și a treia ești afară, și tot așa. Scobitura rămâne goală.
 *
 *  Vârful care cade exact pe dreaptă se numără O SINGURĂ dată: regula
 *  `(va > 0) !== (vb > 0)` pune zeroul mereu de aceeași parte, deci două laturi
 *  care se întâlnesc acolo nu dau două tăieturi în loc de una — iar una în plus
 *  ar inversa par-imparul de acolo încolo și ar umple fix ce trebuia lăsat gol.
 */
export function liniiHasura(inel: number[][], pas: number,
                            unghiDeg: number): number[][] {
  return liniiHasuraMulti([inel], pas, unghiDeg);
}

/** Aceeași hașură, peste MAI MULTE inele deodată.
 *
 *  Par-imparul de mai sus nu face nicio deosebire între contur și gaură — el
 *  numără tăieturi — așa că aceeași regulă răspunde la două întrebări cu totul
 *  diferite: dă-i inelele unei zone și îți hașurează zona; dă-i lotul urmat de
 *  zona edificabilă și îți hașurează exact RESTUL, banda în care nu se poate
 *  construi. Planul de situație o folosește pe a doua, fiindcă aia e
 *  verificarea care se face cu ochiul: corpurile trebuie să cadă în partea
 *  nehașurată.
 *
 *  Un singur inel trece prin aici la fel ca înainte — `liniiHasura` e acum doar
 *  cazul cu unul, ca să nu existe două copii ale regulii care se pot strica
 *  separat. */
export function liniiHasuraMulti(inele: number[][][], pas: number,
                                 unghiDeg: number): number[][] {
  const bune = (inele ?? []).filter((r) => r && r.length >= 3);
  if (!bune.length || !(pas > 0)) return [];
  const t = (unghiDeg * Math.PI) / 180;
  // Direcția liniilor și normala pe ele: proiecția pe normală spune pe ce
  // „bandă" cade fiecare punct, deci între ce valori se așază liniile.
  const [dx, dy] = [Math.cos(t), Math.sin(t)];
  const [nx, ny] = [-dy, dx];

  let vMin = Infinity, vMax = -Infinity;
  for (const inel of bune) {
    for (const [x, y] of inel) {
      const v = x * nx + y * ny;
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }
  }
  if (!(vMax > vMin)) return [];
  // O plasă de zeci de mii de linii nu mai e un desen, e o pată — și aproape
  // întotdeauna înseamnă un pas greșit, nu o zonă mare. De când hașura se
  // cheamă și pe un LOT întreg, nu doar pe un perete, pasul poate veni cu două
  // ordine de mărime greșite fără ca nimeni să observe.
  if ((vMax - vMin) / pas > 20000) return [];

  const out: number[][] = [];
  // Se pornește de la un multiplu al pasului, nu de la marginea zidului:
  // altfel fiecare perete și-ar începe hașura din colțul lui, iar două ziduri
  // în prelungire ar avea liniile decalate — se vede ca o îmbinare greșită.
  const start = Math.ceil(vMin / pas) * pas;
  for (let v = start; v <= vMax; v += pas) {
    const taieturi: number[] = [];
    for (const inel of bune) {
      for (let i = 0; i < inel.length; i++) {
        const a = inel[i], b = inel[(i + 1) % inel.length];
        const va = a[0] * nx + a[1] * ny - v;
        const vb = b[0] * nx + b[1] * ny - v;
        if ((va > 0) === (vb > 0)) continue;      // latura nu traversează
        const s = va / (va - vb);
        taieturi.push((a[0] + (b[0] - a[0]) * s) * dx
                    + (a[1] + (b[1] - a[1]) * s) * dy);
      }
    }
    if (taieturi.length < 2) continue;
    taieturi.sort((p, q) => p - q);
    for (let i = 0; i + 1 < taieturi.length; i += 2) {
      const [u0, u1] = [taieturi[i], taieturi[i + 1]];
      if (u1 - u0 < 1e-6) continue;
      out.push([u0 * dx + v * nx, u0 * dy + v * ny,
                u1 * dx + v * nx, u1 * dy + v * ny]);
    }
  }
  return out;
}

/** Segmentele, adunate într-un singur `d` — v. nota de sus. */
export const caleHasura = (segmente: number[][]): string =>
  segmente.map((s) => `M${s[0].toFixed(3)},${s[1].toFixed(3)}`
                    + `L${s[2].toFixed(3)},${s[3].toFixed(3)}`).join("");

/** Cum se hașurează fiecare ROL de perete.
 *
 *  Materialul NU e în document — `Wall.kind` e rolul — deci tabelul ăsta e o
 *  CONVENȚIE DE DESEN, aleasă după cum se construiește de obicei fiecare rol,
 *  cu simbolurile din tabelul convențional de reprezentare în secțiune:
 *
 *    structural → OȚEL: diagonale la 45°, dese și fine — semnul de oțel din
 *                 tabel. Cerut anume; era hașură încrucișată de beton.
 *    facade     → ZIDĂRIE DEASĂ: diagonale la 45°, pas mediu
 *    party      → ZIDĂRIE: diagonale la 45°, pas larg
 *    partition  → ZIDĂRIE ÎN CELĂLALT SENS (135°). Nu e decor: norma cere ca
 *                 două câmpuri vecine să nu poarte aceeași hașură, altfel un
 *                 despărțitor lipit de o compartimentare se citesc ca unul.
 *    fire       → ÎNCRUCIȘAT DES: nu e un material, e o proprietate.
 *
 *  Cele trei hașuri la 45° se deosebesc prin DESIME — 5, 9 și 14 cm. Așa le
 *  deosebește și tabelul între cărămidă obișnuită, cărămidă aparentă și oțel:
 *  aceeași înclinare, alt pas.
 *
 *  Când `Wall` va purta `material`, tabelul se mută pe el și hașura încetează
 *  să mai fie o convenție. */
export interface FelHasura { unghiuri: number[]; pas: number }

/** HAȘURA ARHITECTURALĂ — una singură, pentru ORICE perete.
 *
 *  ==========================================================================
 *  Cerută de arhitect (19.10.2026): «toți pereții umpluți cu hașură neagră și
 *  să fie toți de tip arhitectural sau zidărie (...) ca să arate mai bine
 *  deocamdată». Adică o planșă de ARHITECTURĂ, unde zidul e zid: pe ea nu se
 *  citește armarea, se citește camera.
 *
 *  NUMAI DESENUL. Rolurile rămân în document și în model neatinse — pe ele
 *  stau FEM-ul, armarea și extrasele (`structure/steps.py`,
 *  `fem_paralel.py`, `tipuri_armare.py`): un perete trecut din `structural` în
 *  `partition` ar schimba ce se armează. `HASURA_ROL` rămâne deci întreg, și
 *  se întoarce la el ștergând o singură linie din `Desen`.
 *
 *  45° la 5 cm: deasă, deci se citește ca o umplutură închisă la mărimea de
 *  lucru, și totuși hașură — la mărire mare liniile se despart, cum trebuie pe
 *  o planșă. ========================================================== */
export const HASURA_ARHITECTURALA: FelHasura = { unghiuri: [45], pas: 0.09 };

export const HASURA_ROL: Readonly<Record<string, FelHasura>> = {
  structural: { unghiuri: [45], pas: 0.05 },
  facade:     { unghiuri: [45], pas: 0.09 },
  party:      { unghiuri: [45], pas: 0.14 },
  partition:  { unghiuri: [135], pas: 0.14 },
  fire:       { unghiuri: [45, 135], pas: 0.08 },
  // peretele de protecție al unui adăpost ALA: beton armat, deci ca structuralul
  ala_perete: { unghiuri: [45], pas: 0.05 },
  // TERMOIZOLAȚIA se hașurează după MATERIAL, nu după rol: ea poartă rolul
  // `facade`, ca zidul de sub ea, iar pe planșă cele două trebuie să se
  // deosebească — altfel stratul de polistiren se citește ca încă un rând de
  // zidărie. Deasă și înclinată invers față de beton, ca să nu se confunde cu el
  // la prima privire.
  polistiren: { unghiuri: [135], pas: 0.05 },
};

export const HASURA_IMPLICITA: FelHasura = { unghiuri: [45], pas: 0.14 };

/** Sub atâția pixeli între două linii, hașura nu mai e hașură, e o pată.
 *
 *  Pragul era pe MĂRIRE (`pxPerM >= 14`), unul pentru toate felurile — dar
 *  desimile diferă de trei ori între ele, iar oțelul, la 5 cm, se strângea într-un
 *  gri mult înainte ca zidăria de 14 să aibă vreo problemă. Pragul e al
 *  DISTANȚEI DE PE ECRAN, deci fiecare fel dispare când chiar nu se mai citește. */
export const MIN_PX_INTRE_LINII = 2.5;
